/**
 * Financial Insights API Slice
 *
 * RTK Query API slice for Financial Insights Dashboard endpoints.
 * Handles data fetching, caching, and state management for financial analytics.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  DashboardKPIs,
  DashboardKPIsRequest,
  RevenueOverTimeData,
  AgingBucket,
  CategoryDataPoint,
  CustomerLeaderboardItem,
  ExpenseTrendsData,
  ProfitTrendData,
  NewVsReturningRevenueData,
  CashFlowData,
} from '../types/financialInsights';

/**
 * Chart granularity options
 */
export interface ChartGranularity {
  granularity?: 'daily' | 'weekly' | 'monthly';
}

/**
 * Customer leaderboard request parameters
 */
export interface CustomerLeaderboardRequest extends DashboardKPIsRequest {
  limit?: number;
}

export const financialInsightsApi = createApi({
  reducerPath: 'financialInsightsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'DashboardKPIs',
    'RevenueOverTime',
    'ARAgingBuckets',
    'APAgingBuckets',
    'CustomerLeaderboard',
    'RevenueByAccount',
    'ExpenseTrends',
    'ProfitTrend',
    'NewVsReturningRevenue',
    'CashFlow',
  ],
  endpoints: builder => ({
    // ========================================================================
    // Dashboard KPIs Endpoint
    // ========================================================================

    /**
     * Get dashboard KPIs for Financial Insights Dashboard
     *
     * Fetches key performance indicators including:
     * - Total Revenue
     * - Cash Balance
     * - Accounts Receivable/Payable
     * - Customer metrics
     * - Payment analytics
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset (this_month, last_month, qtd, ytd, fy, custom)
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @returns Dashboard KPIs data
     */
    getDashboardKPIs: builder.query<DashboardKPIs, DashboardKPIsRequest>({
      query: ({ studioId, preset, start_date, end_date }) => {
        // Build query parameters
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/kpis/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (
        result,
        error,
        { studioId, preset, start_date, end_date }
      ) => [
        {
          type: 'DashboardKPIs',
          id: `${studioId}-${preset}-${start_date || ''}-${end_date || ''}`,
        },
      ],
    }),

    // ========================================================================
    // Revenue Over Time Chart Endpoint
    // ========================================================================

    /**
     * Get revenue over time data with granularity options
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @param granularity - Time granularity (daily, weekly, monthly)
     * @returns Revenue time series data
     */
    getRevenueOverTime: builder.query<
      RevenueOverTimeData,
      DashboardKPIsRequest & ChartGranularity
    >({
      query: ({ studioId, preset, start_date, end_date, granularity }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        if (granularity) {
          params.append('granularity', granularity);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/revenue-over-time/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'RevenueOverTime', id: studioId },
      ],
    }),

    // ========================================================================
    // AR Aging Buckets Endpoint
    // ========================================================================

    /**
     * Get accounts receivable aging buckets
     *
     * @param studioId - Studio UUID
     * @returns AR aging bucket data
     */
    getARAgingBuckets: builder.query<AgingBucket[], { studioId: string }>({
      query: ({ studioId }) =>
        `/studios/${studioId}/financial-insights/ar-aging/`,
      transformResponse: (response: any[]): AgingBucket[] =>
        response.map(bucket => ({
          ...bucket,
          amount: parseFloat(bucket.amount) || 0,
        })),
      providesTags: (result, error, { studioId }) => [
        { type: 'ARAgingBuckets', id: studioId },
      ],
    }),

    // ========================================================================
    // AP Aging Buckets Endpoint
    // ========================================================================

    /**
     * Get accounts payable aging buckets
     *
     * @param studioId - Studio UUID
     * @returns AP aging bucket data
     */
    getAPAgingBuckets: builder.query<AgingBucket[], { studioId: string }>({
      query: ({ studioId }) =>
        `/studios/${studioId}/financial-insights/ap-aging/`,
      transformResponse: (response: any[]): AgingBucket[] =>
        response.map(bucket => ({
          ...bucket,
          amount: parseFloat(bucket.amount) || 0,
        })),
      providesTags: (result, error, { studioId }) => [
        { type: 'APAgingBuckets', id: studioId },
      ],
    }),

    // ========================================================================
    // Customer Leaderboard Endpoint
    // ========================================================================

    /**
     * Get customer leaderboard ranked by revenue
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @param limit - Maximum number of customers to return (default: 10)
     * @returns Customer leaderboard data
     */
    getCustomerLeaderboard: builder.query<
      CustomerLeaderboardItem[],
      CustomerLeaderboardRequest
    >({
      query: ({ studioId, preset, start_date, end_date, limit }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        if (limit) {
          params.append('limit', limit.toString());
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/customer-leaderboard/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      transformResponse: (response: any[]): CustomerLeaderboardItem[] =>
        response.map(customer => ({
          ...customer,
          total_revenue: parseFloat(customer.total_revenue) || 0,
          average_transaction_size:
            parseFloat(customer.average_transaction_size) || 0,
        })),
      providesTags: (result, error, { studioId }) => [
        { type: 'CustomerLeaderboard', id: studioId },
      ],
    }),

    // ========================================================================
    // Revenue by Account Endpoint
    // ========================================================================

    /**
     * Get revenue breakdown by income account
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @returns Revenue by account category data
     */
    getRevenueByAccount: builder.query<
      CategoryDataPoint[],
      DashboardKPIsRequest
    >({
      query: ({ studioId, preset, start_date, end_date }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/revenue-by-account/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'RevenueByAccount', id: studioId },
      ],
    }),

    // ========================================================================
    // Expense Trends Endpoint
    // ========================================================================

    /**
     * Get expense trends with category breakdown
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @returns Expense trends data with categories and time series
     */
    getExpenseTrends: builder.query<ExpenseTrendsData, DashboardKPIsRequest>({
      query: ({ studioId, preset, start_date, end_date }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/expense-trends/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'ExpenseTrends', id: studioId },
      ],
    }),

    // ========================================================================
    // Profit Trend Endpoint
    // ========================================================================

    /**
     * Get profit trend with P&L breakdown
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @param granularity - Time granularity (daily, weekly, monthly)
     * @returns Profit trend data with revenue, COGS, OpEx, and net profit
     */
    getProfitTrend: builder.query<
      ProfitTrendData,
      DashboardKPIsRequest & ChartGranularity
    >({
      query: ({ studioId, preset, start_date, end_date, granularity }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        if (granularity) {
          params.append('granularity', granularity);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/profit-trend/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'ProfitTrend', id: studioId },
      ],
    }),

    // ========================================================================
    // New vs Returning Revenue Endpoint
    // ========================================================================

    /**
     * Get new vs returning customer revenue analysis
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @returns New vs returning revenue data
     */
    getNewVsReturningRevenue: builder.query<
      NewVsReturningRevenueData,
      DashboardKPIsRequest
    >({
      query: ({ studioId, preset, start_date, end_date }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/new-vs-returning/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      transformResponse: (response: any): NewVsReturningRevenueData => ({
        ...response,
        new_customer_revenue: parseFloat(response.new_customer_revenue) || 0,
        returning_customer_revenue:
          parseFloat(response.returning_customer_revenue) || 0,
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'NewVsReturningRevenue', id: studioId },
      ],
    }),

    // ========================================================================
    // Cash Flow Endpoint
    // ========================================================================

    /**
     * Get cash flow analysis
     *
     * @param studioId - Studio UUID
     * @param preset - Date range preset
     * @param start_date - Start date for custom range (YYYY-MM-DD)
     * @param end_date - End date for custom range (YYYY-MM-DD)
     * @param granularity - Time granularity (daily, weekly, monthly)
     * @returns Cash flow data with cash in, cash out, and net cash flow
     */
    getCashFlow: builder.query<
      CashFlowData,
      DashboardKPIsRequest & ChartGranularity
    >({
      query: ({ studioId, preset, start_date, end_date, granularity }) => {
        const params = new URLSearchParams();

        if (preset) {
          params.append('preset', preset);
        }

        if (start_date) {
          params.append('start_date', start_date);
        }

        if (end_date) {
          params.append('end_date', end_date);
        }

        if (granularity) {
          params.append('granularity', granularity);
        }

        const queryString = params.toString();
        const url = `/studios/${studioId}/financial-insights/cash-flow/${
          queryString ? `?${queryString}` : ''
        }`;

        return url;
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'CashFlow', id: studioId },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetDashboardKPIsQuery,
  useLazyGetDashboardKPIsQuery,
  useGetRevenueOverTimeQuery,
  useGetARAgingBucketsQuery,
  useGetAPAgingBucketsQuery,
  useGetCustomerLeaderboardQuery,
  useGetRevenueByAccountQuery,
  useGetExpenseTrendsQuery,
  useGetProfitTrendQuery,
  useGetNewVsReturningRevenueQuery,
  useGetCashFlowQuery,
} = financialInsightsApi;
